import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { DialogEnums } from "models/enums";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import BenefitCreateFormContainer from "./benefitCreateFormContainer";
import BenefitUpdateFormContainer from "./benefitUpdateFormContainer";
import { useDeviationPermissions } from "components/salaryDeviation";
import useSalaryReApproveDialog from "components/wintSalary/useSalaryReApproveDialog";
//#endregion
import { IconDefinitionConstants } from "utils/constants";
import { LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";

function useBenefitDialog() {
  const dispatch = useDispatch();
  const getReApproveDialog = useSalaryReApproveDialog();

  const employeesRef = React.useRef<AutocompleteListItem[] | undefined>(undefined);

  const handleFormUpdate = React.useCallback((values: any) => {
    employeesRef.current = values?.employees;
  }, []);

  const showReApproveDialogIfNeeded = (
    result: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase | undefined,
    onSuccess?: () => void
  ) => {
    const reApproveData = result?.reApprovalInfo;
    if (reApproveData) {
      const { personId, yearMonth } = reApproveData;

      const personName = employeesRef.current?.find((e) => Number(e.id) === personId)?.value ?? "";

      const reApproveDialog = getReApproveDialog(
        personId,
        personName,
        yearMonth,
        false,
        undefined,
        undefined,
        onSuccess
      );

      setTimeout(() => {
        dispatch(displayDialog({ ...reApproveDialog, onClose: onSuccess }));
      }, 100);
    } else {
      onSuccess?.();
    }
  };

  const getBenefitCreationDialog = (onSubmitSuccess?: () => void): DialogProps => {
    const handleSubmit = (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => {
      showReApproveDialogIfNeeded(result, onSubmitSuccess);
    };

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.AddBenefit),
      titleIcon: IconDefinitionConstants.WGifts,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Submit),
          successMessage: TranslationKeys.Benefit.BenefitCreatedSuccess,
        },
      ],
      dialogType: DialogEnums.DialogType.Content,
      children: <BenefitCreateFormContainer onFormUpdate={handleFormUpdate} />,
      maxWidth: "sm",
      onSubmit: handleSubmit,
    };
  };

  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();
  const readonlyUser = !(isSalaryAdmin || isDeviationAdmin || isBusinessUnitManagerWithSalaryRights);

  const getBenefitUpdateDialog = (benefitId: string, onSubmitSuccess?: () => void): DialogProps => {
    const handleSubmit = (result?: WintSuperkollAPIModelWintSalary.SalaryMonthMutationResultBase) => {
      showReApproveDialogIfNeeded(result, onSubmitSuccess);
    };

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.UpdateBenefit),
      titleIcon: IconDefinitionConstants.WGifts,
      actions: !readonlyUser
        ? [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Submit),
              successMessage: TranslationKeys.Benefit.BenefitUpdatedSuccess,
            },
          ]
        : undefined,
      dialogType: DialogEnums.DialogType.Content,
      children: <BenefitUpdateFormContainer benefitId={benefitId} readonly={readonlyUser} onFormUpdate={handleFormUpdate} />,
      maxWidth: "sm",
      onSubmit: handleSubmit,
    };
  };

  return { getBenefitCreationDialog, getBenefitUpdateDialog };
}

export default useBenefitDialog;
